import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { fetchAnalyticsClients, fetchAnalyticsClientsStats } from '../api';
import { getTexts } from '../utils/configLoader';
import { Eye, Phone, Angry } from 'lucide-react';
import { format } from 'date-fns';
import ClientDetailsModal from './ClientDetailsModal';

const PAGE_SIZE = 20;

const moscowDateTimeFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: 'Europe/Moscow',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : moscowDateTimeFormatter.format(date);
};

const toDateParam = (value) => (value ? format(value, 'yyyy-MM-dd') : undefined);

const Clients = ({ dateRange }) => {
  const texts = getTexts('clients');

  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyComplaints, setOnlyComplaints] = useState(false);
  const [onlyNegativeClients, setOnlyNegativeClients] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  const dateParams = useMemo(
    () => ({ date_from: toDateParam(dateRange?.from), date_to: toDateParam(dateRange?.to) }),
    [dateRange?.from, dateRange?.to]
  );

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchAnalyticsClientsStats({ ...dateParams });
      setStats(data);
    } catch (err) {
      console.error(err);
    }
  }, [dateParams]);

  const loadClients = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchAnalyticsClients({
        ...dateParams,
        search: searchQuery || undefined,
        only_complaints: onlyComplaints || undefined,
        only_negative_clients: onlyNegativeClients || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE
      });
      setClients(response.items ?? []);
      setTotal(response.total ?? 0);
    } catch (err) {
      console.error(err);
      setError('Не удалось загрузить список клиентов.');
    } finally {
      setIsLoading(false);
    }
  }, [dateParams, searchQuery, onlyComplaints, onlyNegativeClients, page]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{texts.title}</CardTitle>
          <CardDescription>{texts.description}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="flex flex-1 gap-2">
            <Input
              placeholder="Поиск по номеру телефона"
              value={searchQuery}
              onChange={(event) => {
                setPage(1);
                setSearchQuery(event.target.value);
              }}
            />
            <Button variant="outline" onClick={() => { setSearchQuery(''); setPage(1); }}>
              Сброс
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="only-complaints"
              checked={onlyComplaints}
              onCheckedChange={(value) => { setOnlyComplaints(value); setPage(1); }}
            />
            <label htmlFor="only-complaints" className="text-sm font-medium">Только с жалобами</label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="only-negative-clients"
              checked={onlyNegativeClients}
              onCheckedChange={(value) => { setOnlyNegativeClients(value); setPage(1); }}
            />
            <label htmlFor="only-negative-clients" className="text-sm font-medium">Только клиенты с повторными жалобами</label>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        <StatCard title="Клиентов" value={(stats?.totalClients ?? 0).toLocaleString()} />
        <StatCard title="Звонков/клиент" value={stats?.avgCallsPerClient ?? 0} />
        <StatCard title="С жалобами" value={`${stats?.complaintClients ?? 0} (${stats?.complaintClientsPercent ?? 0}%)`} />
        <StatCard title="Повторные" value={`${stats?.repeatClients ?? 0} (${stats?.repeatClientsPercent ?? 0}%)`} />
        <StatCard title="Повторных жалоб" value={(stats?.angryClients ?? 0).toLocaleString()} />
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <CardTitle>Клиенты за период</CardTitle>
          <div className="text-sm text-muted-foreground">
            Страница {page} из {totalPages} &middot; Всего {total.toLocaleString()}
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Телефон</TableHead>
                  <TableHead>Звонки</TableHead>
                  <TableHead>Сигналы</TableHead>
                  <TableHead>Последний звонок</TableHead>
                  <TableHead className="text-right">Детали</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      Загрузка...
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && clients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      Нет клиентов за выбранный период.
                    </TableCell>
                  </TableRow>
                )}
                {clients.map((client) => (
                  <TableRow key={client.clientId}>
                    <TableCell className="font-medium">{client.phone || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="gap-1 border-sky-200 bg-sky-50 text-sky-800">
                        <Phone className="h-3 w-3" />
                        {client.totalCalls ?? 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <ClientSignals client={client} />
                    </TableCell>
                    <TableCell>{formatDateTime(client.lastCall)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setSelectedClient(client)}>
                        <Eye className="mr-2 h-4 w-4" /> Детали
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Показано {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, total)} из {total}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>
                Назад
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}>
                Вперёд
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <ClientDetailsModal
        client={selectedClient}
        dateRange={dateRange}
        onClose={() => setSelectedClient(null)}
      />
    </div>
  );
};

const ClientSignals = ({ client }) => {
  const hasComplaint = (client.complaintsCount ?? 0) > 0;
  const hasNegative = (client.negativeCount ?? 0) > 0;
  const hasComplaintSignal = hasComplaint || hasNegative;
  const hasComplaintToday = client.complaintToday || client.negativeToday;

  if (!hasComplaintSignal && !client.isAngry) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {client.isAngry && (
        <Angry
          className="h-4 w-4 text-red-600"
          aria-label="Клиент с повторными жалобами"
          title="Клиент с повторными жалобами более чем в один день за период"
        />
      )}
      {hasComplaintSignal && (
        <Badge
          className={hasComplaintToday
            ? 'bg-red-600 text-white hover:bg-red-600'
            : 'bg-red-100 text-red-700 hover:bg-red-100'}
        >
          Жалоба{hasComplaintToday ? ' · сегодня' : ''}
        </Badge>
      )}
    </div>
  );
};

const StatCard = ({ title, value }) => (
  <Card className="p-3">
    <div className="text-xs font-medium text-muted-foreground">{title}</div>
    <div className="mt-1 text-lg font-bold">{value}</div>
  </Card>
);

export default Clients;
